import { useEffect, useCallback, useRef } from 'react';
import { performanceMonitoring, memoryOptimization, bundleOptimization } from '../utils/assetOptimization';

interface PerformanceOptimizationOptions {
  enableMemoryMonitoring?: boolean;
  enableCoreWebVitals?: boolean;
  memoryCleanupInterval?: number;
  enableResourceTiming?: boolean;
}

/**
 * Hook for comprehensive performance optimization
 */
export function usePerformanceOptimization(options: PerformanceOptimizationOptions = {}) {
  const {
    enableMemoryMonitoring = true,
    enableCoreWebVitals = true,
    memoryCleanupInterval = 30000, // 30 seconds
    enableResourceTiming = true,
  } = options;

  const cleanupIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Initialize performance monitoring
  useEffect(() => {
    if (enableCoreWebVitals) {
      performanceMonitoring.measureCoreWebVitals();
    }

    if (enableResourceTiming) {
      // Log resource timing after page load
      const logResourceTiming = () => {
        const resources = performanceMonitoring.measureResourceTiming();
        console.log('Resource Timing:', resources);
      };

      if (document.readyState === 'complete') {
        logResourceTiming();
      } else {
        window.addEventListener('load', logResourceTiming);
        return () => window.removeEventListener('load', logResourceTiming);
      }
    }
  }, [enableCoreWebVitals, enableResourceTiming]);

  // Memory monitoring and cleanup
  useEffect(() => {
    if (!enableMemoryMonitoring) return;

    const performMemoryCleanup = () => {
      const memInfo = memoryOptimization.getMemoryInfo();
      
      if (memInfo) {
        console.log('Memory Usage:', memInfo);
        
        // Perform cleanup if memory usage is high
        if (memoryOptimization.isMemoryUsageHigh()) {
          console.warn('High memory usage detected, performing cleanup...');
          memoryOptimization.cleanup();
        }
      }
    };

    // Initial check
    performMemoryCleanup();

    // Set up periodic cleanup
    cleanupIntervalRef.current = setInterval(performMemoryCleanup, memoryCleanupInterval);

    return () => {
      if (cleanupIntervalRef.current) {
        clearInterval(cleanupIntervalRef.current);
      }
    };
  }, [enableMemoryMonitoring, memoryCleanupInterval]);

  // Performance utilities
  const getNavigationTiming = useCallback(() => {
    return performanceMonitoring.getNavigationTiming();
  }, []);

  const getMemoryInfo = useCallback(() => {
    return memoryOptimization.getMemoryInfo();
  }, []);

  const shouldLoadFeature = useCallback((featureName: string) => {
    return bundleOptimization.shouldLoadFeature(featureName);
  }, []);

  const getChunkLoadingStrategy = useCallback(() => {
    return bundleOptimization.getChunkLoadingStrategy();
  }, []);

  const forceCleanup = useCallback(() => {
    memoryOptimization.cleanup();
  }, []);

  return {
    getNavigationTiming,
    getMemoryInfo,
    shouldLoadFeature,
    getChunkLoadingStrategy,
    forceCleanup,
  };
}

/**
 * Hook for optimizing component re-renders
 */
export function useRenderOptimization() {
  const renderCountRef = useRef(0);
  const lastRenderTimeRef = useRef(Date.now());

  useEffect(() => {
    renderCountRef.current += 1;
    const now = Date.now();
    const timeSinceLastRender = now - lastRenderTimeRef.current;
    lastRenderTimeRef.current = now;

    // Log excessive re-renders
    if (renderCountRef.current > 10 && timeSinceLastRender < 100) {
      console.warn(`Component re-rendered ${renderCountRef.current} times. Consider optimization.`);
    }
  });

  const getRenderStats = useCallback(() => {
    return {
      renderCount: renderCountRef.current,
      lastRenderTime: lastRenderTimeRef.current,
    };
  }, []);

  return { getRenderStats };
}

/**
 * Hook for lazy loading resources based on user interaction
 */
export function useLazyResourceLoading() {
  const loadedResourcesRef = useRef<Set<string>>(new Set());

  const loadResource = useCallback(async (resourceId: string, loader: () => Promise<any>) => {
    if (loadedResourcesRef.current.has(resourceId)) {
      return; // Already loaded
    }

    try {
      await loader();
      loadedResourcesRef.current.add(resourceId);
    } catch (error) {
      console.error(`Failed to load resource ${resourceId}:`, error);
    }
  }, []);

  const isResourceLoaded = useCallback((resourceId: string) => {
    return loadedResourcesRef.current.has(resourceId);
  }, []);

  return {
    loadResource,
    isResourceLoaded,
  };
}

/**
 * Hook for adaptive loading based on network conditions
 */
export function useAdaptiveLoading() {
  const getNetworkInfo = useCallback(() => {
    const connection = (navigator as any).connection;
    if (!connection) return null;

    return {
      effectiveType: connection.effectiveType,
      downlink: connection.downlink,
      rtt: connection.rtt,
      saveData: connection.saveData,
    };
  }, []);

  const shouldLoadHeavyContent = useCallback(() => {
    const networkInfo = getNetworkInfo();
    if (!networkInfo) return true; // Default to loading if no info available

    // Don't load heavy content on slow connections or data saver mode
    if (networkInfo.saveData || networkInfo.effectiveType === 'slow-2g' || networkInfo.effectiveType === '2g') {
      return false;
    }

    return true;
  }, [getNetworkInfo]);

  const getOptimalImageQuality = useCallback(() => {
    const networkInfo = getNetworkInfo();
    if (!networkInfo) return 80; // Default quality

    if (networkInfo.saveData || networkInfo.effectiveType === 'slow-2g') {
      return 40; // Low quality for slow connections
    }

    if (networkInfo.effectiveType === '2g' || networkInfo.effectiveType === '3g') {
      return 60; // Medium quality
    }

    return 80; // High quality for fast connections
  }, [getNetworkInfo]);

  return {
    getNetworkInfo,
    shouldLoadHeavyContent,
    getOptimalImageQuality,
  };
}

export default usePerformanceOptimization;